import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tab,
} from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceTabs } from '~/components/SolaForceTabs';
import { AbsenceType } from '~/data/absences/types';
import { AbsenceFormFields } from '~/features/AbsenceChanges/EditAbsenceDialog/components/AbsenceFormFields';
import { useAbsenceForm } from '~/features/AbsenceChanges/EditAbsenceDialog/useAbsenceForm';
import { useEditAbsenceDialogData } from '~/features/AbsenceChanges/EditAbsenceDialog/useEditAbsenceDialogData';
import { AbsencesTabContent_OnUpdateLegacy } from '~/library/AbsencesTabContent/component';

import { useSxStyles } from './useStyles';

export type EditAbsenceDialogProps = {
  employeeId: number;
  absenceId?: string;
  absenceType?: AbsenceType;
  onClose: () => void;
  open: boolean;
} & AbsencesTabContent_OnUpdateLegacy;

export const EditAbsenceDialog = (props: EditAbsenceDialogProps) => {
  const { employeeId, absenceId, absenceType, onClose, open, onUpdateLegacy } =
    props;

  const { t } = useTranslation();

  const sxStyles = useSxStyles();

  const {
    categoryTabs,
    selectedAbsenceType,
    formProps,
    isLoading: isLoadingData,
    failedToLoad: failedToLoadData,
  } = useEditAbsenceDialogData({
    employeeId,
    absenceId,
    absenceType,
    onUpdateLegacy,
  });

  const {
    absenceFormFieldProps,
    isSubmitting,
    isLoading,
    failedToLoad,
    handleFormSubmit,
    handleClose,
  } = useAbsenceForm({ employeeId, onClose, formProps });
  const isDirty = absenceFormFieldProps.formMethods.formState.isDirty;

  const editMode = !!formProps.selectedAbsence;

  const isFormReady =
    !isLoading && !failedToLoad && !isLoadingData && !failedToLoadData;

  return (
    <DraggableDialog open={open} onClose={handleClose}>
      <DialogTitle>
        {editMode
          ? t('editAbsence')
          : `${t('newAbsence')}${
              categoryTabs.enabled
                ? ''
                : ` - ${t(
                    selectedAbsenceType === AbsenceType.LONG
                      ? 'laLong'
                      : 'laShort',
                  )}`
            }`}
      </DialogTitle>
      <Divider />
      {categoryTabs.enabled && (
        <SolaForceTabs
          value={categoryTabs.selected}
          onChange={(_event, value) => categoryTabs.onTabChange(value)}
        >
          {Object.values(AbsenceType).map((type, index) => (
            <Tab
              key={type}
              label={t(type === AbsenceType.LONG ? 'laLong' : 'laShort')}
              value={type}
              data-cy={type}
            />
          ))}
        </SolaForceTabs>
      )}

      <DialogContent sx={sxStyles.dialogContentRoot}>
        {isLoading && <CircularProgress sx={sxStyles.spinner} />}
        {isFormReady && (
          <AbsenceFormFields
            absenceType={selectedAbsenceType}
            {...absenceFormFieldProps}
          />
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          color="primary"
          onClick={handleClose}
          data-testid="editAbs-button-cancel"
        >
          {t('cancel')}
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={isSubmitting || !isDirty}
          onClick={handleFormSubmit}
          data-testid="editAbs-button-submit"
        >
          {t('laSubmit')}
        </Button>
      </DialogActions>
    </DraggableDialog>
  );
};
